'use client';

import { cn } from '@lib/util/cn';
import { PlaceholderImage } from '@modules/common/icons';
import Image, { type ImageProps } from 'next/image';
import { useEffect, useRef, useState } from 'react';

export const LoadingImage = ({
  src,
  alt,
  quality = 75,
  className,
  onClick,
  hideSkeleton = false,
  preload,
  wrapperClassName,
  placeholderIconSize = 48,
  ...imageProps
}: {
  className?: string;
  onClick?: () => void;
  quality?: number;
  alt: string;
  src: string | undefined;
  hideSkeleton?: boolean;
  wrapperClassName?: string;
  placeholderIconSize?: number;
} & Omit<ImageProps, 'src'>): React.JSX.Element => {
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>(
    'loading',
  );
  const [mounted, setMounted] = useState(false);
  const prevSrcRef = useRef(src);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (prevSrcRef.current !== src) {
    prevSrcRef.current = src;
    setStatus('loading');
  }

  const showPlaceholder = status === 'error' || !src;
  const showSkeleton =
    status === 'loading' && !hideSkeleton && !showPlaceholder;

  const inner = (
    <>
      {showSkeleton && (
        <div className="absolute inset-0 animate-pulse bg-ui-bg-component" />
      )}
      {showPlaceholder ? (
        <div
          aria-label={alt}
          className="flex h-full w-full items-center justify-center"
          role="img"
        >
          <PlaceholderImage color="#9ca3af" size={placeholderIconSize} />
        </div>
      ) : (
        <Image
          alt={alt}
          className={cn(
            mounted && status === 'loading' && !hideSkeleton && 'opacity-0',
            className,
          )}
          onError={() => setStatus('error')}
          onLoad={() => setStatus('loaded')}
          quality={quality}
          src={src}
          {...(preload ? { preload: true } : {})}
          {...imageProps}
        />
      )}
    </>
  );

  const wrapperClass = cn(
    wrapperClassName ?? 'relative h-full w-full',
    onClick && 'cursor-pointer',
  );

  if (onClick) {
    return (
      <button
        aria-label={alt}
        className={wrapperClass}
        onClick={onClick}
        type="button"
      >
        {inner}
      </button>
    );
  }

  return <div className={wrapperClass}>{inner}</div>;
};
